import React, { useMemo, useEffect } from 'react';
import { ContentWrapper, DashboardWrapper as DashboardWrapperStyled, Wrapper } from './styled';
import { useAtomValue, Provider, useSetAtom } from 'jotai';
import {
  isDashboardGeneratedAtom,
  panelWidthAtom,
  isReportPanelOpenAtom,
} from '@src/components/nexa/NexaStore';
import { buildDashboardStore } from '@src/atoms/utils';
import useCheckDashboardEntry from '@src/components/nexa/hooks/useCheckDashboardEntry';
import ReportPanel from '@src/components/nexa/Report/ReportPanel';
import DashboardWrapper from '@src/components/nexa/Dashboard/DashboardWrapper';

interface NexaWrapperProps {
  children: React.ReactNode;
  storeKey: string | null | undefined;
}

export const NexaWrapper: React.FC<NexaWrapperProps> = ({ children, storeKey }) => {
  const store = useMemo(() => {
    return buildDashboardStore();
  }, [storeKey]); // resets store on storeKey change

  return (
    <Provider store={store}>
      <NexaLayout storeKey={storeKey}>{children}</NexaLayout>
    </Provider>
  );
};

interface NexaLayoutProps {
  children: React.ReactNode;
  storeKey: string | null | undefined;
}

const NexaLayout: React.FC<NexaLayoutProps> = ({ children, storeKey }) => {
  const widthPercent = useAtomValue(panelWidthAtom);
  const isDashboardGenerated = useAtomValue(isDashboardGeneratedAtom);
  const isReportPanelOpen = useAtomValue(isReportPanelOpenAtom);
  const setPanelWidth = useSetAtom(panelWidthAtom);
  const setIsDashboardGenerated = useSetAtom(isDashboardGeneratedAtom);

  // Check if dashboard exists when storeKey is provided
  useCheckDashboardEntry({ id: storeKey });

  // Adjust panel width when report panel opens/closes
  useEffect(() => {
    if (isReportPanelOpen) {
      setPanelWidth(30);
      // Close dashboard when report panel opens
      setIsDashboardGenerated(false);
    } else if (!isDashboardGenerated && !isReportPanelOpen) {
      // If neither panel is open, set to full width
      setPanelWidth(100);
    }
  }, [isReportPanelOpen, isDashboardGenerated, setPanelWidth, setIsDashboardGenerated]);

  // Determine what to show in the side panel
  const showDashboard = isDashboardGenerated && !isReportPanelOpen;
  const showReport = isReportPanelOpen;
  const showPanel = showDashboard || showReport;

  return (
    <Wrapper>
      <ContentWrapper width={widthPercent}>{children}</ContentWrapper>
      {showPanel && (
        <DashboardWrapperStyled>
          {showReport ? (
            <ReportPanel />
          ) : showDashboard ? (
            <DashboardWrapper />
          ) : (
            <div>No dashboards available to display at the moment.</div>
          )}
        </DashboardWrapperStyled>
      )}
    </Wrapper>
  );
};
